import React, {
  DetailedHTMLProps,
  DOMAttributes,
  FC,
  HTMLAttributes,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState
} from 'react';
import ZoomMediaContext from 'contexts/ZoomMediaContext';
import ZoomVideo, {
  ConnectionState,
  ReconnectReason,
  VideoPlayer,
  VideoPlayerContainer
} from '@zoom/videosdk';
import { produce } from 'immer';
import { MediaStream } from '../zoom.config';
import ZoomContext from 'contexts/ZoomContext';
import { Outlet } from 'react-router-dom';
import { IConsultationDetails } from 'utils/models';
import { logZoomErrorToSentry, logZoomEventToSentry } from 'utils/helpers';
import LoaderSpinner from 'components/LoaderSpinner';

type CustomElement<T> = Partial<T & DOMAttributes<T> & { children: any }>;

interface IZoomVideoOutlet {
  meetingArgs: {
    sdkKey: string;
    topic: string;
    signature: string;
    name: string;
    password?: string;
    webEndpoint?: string;
    enforceGalleryView?: string;
    enforceVB?: string;
    customerJoinId?: string;
    lang?: string;
    useVideoPlayer?: string;
  };
  consultationDetails: IConsultationDetails | undefined;
  isError?: boolean;
}

const mediaShape = {
  audio: { encode: false, decode: false },
  video: { encode: false, decode: false },
  share: { encode: false, decode: false }
};

const mediaReducer = produce((draft, action) => {
  const [type, actionType] = action.type?.split('-') || [];
  // @ts-ignore
  if (type && actionType && draft[type]) {
    // @ts-ignore
    draft[type][actionType] = action.payload;
  } else if (action.type === 'reset-media') {
    Object.assign(draft, { ...mediaShape });
  }
}, mediaShape);

declare global {
  interface Window {
    webEndpoint: string | undefined;
    zmClient: any | undefined;
    mediaStream: any | undefined;
    crossOriginIsolated: boolean;
    ltClient: any | undefined;
    logClient: any | undefined;
  }

  namespace JSX {
    interface IntrinsicElements {
      ['video-player']: DetailedHTMLProps<HTMLAttributes<VideoPlayer>, VideoPlayer> & {
        class?: string;
      };
      ['video-player-container']: CustomElement<VideoPlayerContainer> & { class?: string };
    }
  }
}

const ZoomVideoOutlet: FC<IZoomVideoOutlet> = ({ meetingArgs, consultationDetails, isError }) => {
  const {
    topic,
    signature,
    name,
    password,
    webEndpoint: webEndpointArg,
    enforceGalleryView,
    enforceVB,
    useVideoPlayer
  } = meetingArgs;

  // Ensure all required parameters are strings
  const { safeTopic, safeSignature, safeName, safePassword } = useMemo(
    () => ({
      safeTopic: typeof topic === 'string' ? topic : '',
      safeSignature: typeof signature === 'string' ? signature : '',
      safeName: typeof name === 'string' ? name : '',
      safePassword: typeof password === 'string' ? password : undefined
    }),
    [topic, signature, name, password]
  );

  const [loading, setIsLoading] = useState(true);
  const [loadingText, setLoadingText] = useState('Initializing consultation...');
  const [isFailover, setIsFailover] = useState(false);
  const [status, setStatus] = useState('');
  const [mediaState, dispatch] = useReducer(mediaReducer, mediaShape);
  const [mediaStream, setMediaStream] = useState<MediaStream | null>(null);
  const [isSupportGalleryView, setIsSupportGalleryView] = useState(false);
  const zmClient = useContext(ZoomContext);
  const isJoining = useRef(false);

  const webEndpoint = webEndpointArg ?? window?.webEndpoint ?? 'zoom.us';

  const mediaContext = useMemo(() => ({ ...mediaState, mediaStream }), [mediaState, mediaStream]);
  const galleryViewWithoutSAB = Number(enforceGalleryView) === 1 && !window.crossOriginIsolated;
  const vbWithoutSAB = Number(enforceVB) === 1 && !window.crossOriginIsolated;
  const galleryViewWithAttach =
    Number(useVideoPlayer) === 1 && (window.crossOriginIsolated || galleryViewWithoutSAB);

  useEffect(() => {
    const init = async () => {
      if (isJoining.current) {
        console.log('[Zoom Init] Already joining, skipping...');
        return;
      }

      const sessionInfo = zmClient.getSessionInfo();
      if (sessionInfo?.sessionId) {
        console.log('[Zoom Init] Already joined, skipping init.');
        setIsLoading(false);
        return;
      }

      try {
        isJoining.current = true;
        console.log('[Zoom Init] Starting init...');

        // Add a small delay to ensure any previous operations are complete
        await new Promise((resolve) => setTimeout(resolve, 500));

        // Ensure client is properly destroyed before reinitializing
        try {
          await zmClient.leave();
        } catch (leaveError) {
          console.log('[Zoom Init] Leave attempt (cleanup):', leaveError);
          // This is expected if not in a meeting
        }

        console.log('[Zoom Init] Initializing SDK...');
        await zmClient.init('en-US', `${window.location.origin}/lib`, {
          webEndpoint,
          enforceMultipleVideos: galleryViewWithoutSAB,
          enforceVirtualBackground: vbWithoutSAB,
          stayAwake: true,
          patchJsMedia: true,
          leaveOnPageUnload: false
        });

        console.log('[Zoom Init] Joining session...');
        console.log('[Zoom Init] Original Parameters:', {
          topic: topic,
          topicType: typeof topic,
          signature: signature ? '[REDACTED]' : 'MISSING',
          signatureType: typeof signature,
          name: name,
          nameType: typeof name,
          password: password ? '[REDACTED]' : 'MISSING',
          passwordType: typeof password
        });

        console.log('[Zoom Init] Safe Parameters:', {
          safeTopic: safeTopic,
          safeSignature: safeSignature ? '[REDACTED]' : 'MISSING',
          safeName: safeName,
          safePassword: safePassword ? '[REDACTED]' : 'MISSING'
        });

        // Validate required parameters
        if (!safeTopic) {
          const error = new Error(`Invalid topic: Original=${topic}, Safe=${safeTopic}`);
          logZoomErrorToSentry(error, {
            component: 'ZoomVideoOutlet',
            action: 'validateJoinParameters',
            consultationId: consultationDetails?.id,
            referenceId: consultationDetails?.reference_id,
            parameterName: 'topic',
            originalValue: topic,
            safeValue: safeTopic
          });
          throw error;
        }
        if (!safeSignature) {
          const error = new Error(
            `Invalid signature: Original=${signature ? '[REDACTED]' : 'MISSING'}, Safe=${
              safeSignature ? '[REDACTED]' : 'MISSING'
            }`
          );
          logZoomErrorToSentry(error, {
            component: 'ZoomVideoOutlet',
            action: 'validateJoinParameters',
            consultationId: consultationDetails?.id,
            referenceId: consultationDetails?.reference_id,
            parameterName: 'signature'
          });
          throw error;
        }
        if (!safeName) {
          const error = new Error(`Invalid name: Original=${name}, Safe=${safeName}`);
          logZoomErrorToSentry(error, {
            component: 'ZoomVideoOutlet',
            action: 'validateJoinParameters',
            consultationId: consultationDetails?.id,
            referenceId: consultationDetails?.reference_id,
            parameterName: 'name',
            originalValue: name,
            safeValue: safeName
          });
          throw error;
        }

        await zmClient.join(safeTopic, safeSignature, safeName, safePassword);

        const stream = zmClient.getMediaStream();
        console.log('[Zoom Init] Stream obtained');
        setMediaStream(stream);
        setIsSupportGalleryView(stream.isSupportMultipleVideos());
        setIsLoading(false);
      } catch (e: any) {
        console.error('[Zoom Join Error]', e);

        // Handle specific error cases
        if (e.type === 'OPERATION_CANCELLED' && e.reason === 'LEAVING_MEETING') {
          console.log('[Zoom Init] Client was leaving, destroying and retrying...');
          logZoomEventToSentry(
            'Zoom client was in LEAVING_MEETING state, retrying after cleanup',
            'warning',
            {
              component: 'ZoomVideoOutlet',
              action: 'handleLeavingMeetingError',
              consultationId: consultationDetails?.id,
              referenceId: consultationDetails?.reference_id,
              errorType: e.type,
              errorReason: e.reason
            }
          );
          try {
            // Force destroy the client and create a new one
            ZoomVideo.destroyClient();
            await new Promise((resolve) => setTimeout(resolve, 1000));
          } catch (destroyError) {
            console.log('[Zoom Init] Destroy error (expected):', destroyError);
          }

          setTimeout(() => {
            if (consultationDetails && !isJoining.current) {
              console.log('[Zoom Init] Retrying after LEAVING_MEETING error...');
              init();
            }
          }, 2000);
          return;
        }

        // Log all other join errors to Sentry
        logZoomErrorToSentry(e, {
          component: 'ZoomVideoOutlet',
          action: 'joinSession',
          consultationId: consultationDetails?.id,
          referenceId: consultationDetails?.reference_id,
          errorType: e.type,
          errorReason: e.reason,
          errorMessage: e.message,
          topic: safeTopic,
          name: safeName
        });

        setLoadingText(`Join failed: ${e.reason || 'Unknown error'}`);
        setIsLoading(false);
      } finally {
        isJoining.current = false;
      }
    };

    if (consultationDetails) {
      init();
    }

    return () => {
      ZoomVideo.destroyClient();
    };
  }, [
    zmClient,
    topic,
    signature,
    name,
    password,
    webEndpoint,
    galleryViewWithoutSAB,
    vbWithoutSAB,
    consultationDetails,
    safeTopic,
    safeSignature,
    safeName,
    safePassword
  ]);

  const onConnectionChange = useCallback(
    (payload: any) => {
      console.log('[Connection Change]', payload);
      const { state, reason, subsessionName } = payload;

      switch (state) {
        case ConnectionState.Reconnecting:
          setIsLoading(true);
          setIsFailover(true);
          setStatus('connecting');
          if (reason === ReconnectReason.Failover) {
            logZoomEventToSentry('Zoom session reconnecting due to failover', 'warning', {
              component: 'ZoomVideoOutlet',
              action: 'connectionReconnecting',
              consultationId: consultationDetails?.id,
              referenceId: consultationDetails?.reference_id,
              reason: 'Failover'
            });
            setLoadingText('Session disconnected. Trying to reconnect...');
          } else if (reason === ReconnectReason.JoinSubsession) {
            setLoadingText(`Joining ${subsessionName}...`);
          } else if (reason === ReconnectReason.BackToMainSession) {
            setLoadingText('Returning to main session...');
          }
          break;

        case ConnectionState.Connected:
          setStatus('connected');
          setIsLoading(false);
          window.zmClient = zmClient;
          window.mediaStream = zmClient.getMediaStream();
          break;

        case ConnectionState.Closed:
          setStatus('closed');
          dispatch({ type: 'reset-media' });
          if (reason === 'ended by host') {
            logZoomEventToSentry('Zoom session ended by host', 'info', {
              component: 'ZoomVideoOutlet',
              action: 'connectionClosed',
              consultationId: consultationDetails?.id,
              referenceId: consultationDetails?.reference_id,
              reason
            });
          }
          setLoadingText(reason === 'ended by host' ? 'Session ended by host.' : 'Session closed.');
          break;

        case ConnectionState.Fail:
          setStatus('fail');
          logZoomErrorToSentry(new Error('Zoom connection failed - unable to reconnect'), {
            component: 'ZoomVideoOutlet',
            action: 'connectionFailed',
            consultationId: consultationDetails?.id,
            referenceId: consultationDetails?.reference_id,
            reason,
            state: 'Fail'
          });
          setLoadingText('Unable to reconnect. Please reload.');
          break;
      }
    },

    // eslint-disable-next-line react-hooks/exhaustive-deps
    [isFailover, zmClient, consultationDetails]
  );

  const onMediaSDKChange = useCallback((payload: any) => {
    const { action, type, result } = payload;
    dispatch({ type: `${type}-${action}`, payload: result === 'success' });
  }, []);

  const onLeaveOrJoinSession = useCallback(async () => {
    if (status === 'closed') {
      setIsLoading(true);
      await zmClient.join(safeTopic, safeSignature, safeName, safePassword);
      setIsLoading(false);
    } else if (status === 'connected') {
      await zmClient.leave();
    }
  }, [status, zmClient, safeTopic, safeSignature, safeName, safePassword]);

  const handleEndSession = async () => await zmClient.leave();

  useEffect(() => {
    zmClient.on('connection-change', onConnectionChange);
    zmClient.on('media-sdk-change', onMediaSDKChange);
    return () => {
      zmClient.off('connection-change', onConnectionChange);
      zmClient.off('media-sdk-change', onMediaSDKChange);
    };
  }, [zmClient, onConnectionChange, onMediaSDKChange]);

  useEffect(() => {
    if (isError) {
      setTimeout(() => {
        setTimeout(() => window.location.replace(`${window.location.origin}/dashboard`), 1500);
      }, 2000);
    }
  }, [isError]);

  return (
    <div className="h-screen flex flex-col ">
      {loading && (
        <div className="h-full flex justify-center items-center">
          <LoaderSpinner
            className="w-10 h-10"
            loadingText={
              isError
                ? 'You are not authorized to make this consultation, you are being redirected.'
                : loadingText
            }
          />
        </div>
      )}
      {!loading && !isError && (
        <ZoomMediaContext.Provider value={mediaContext}>
          <Outlet
            context={{
              status,
              onLeaveOrJoinSession,
              handleEndSession,
              isSupportGalleryView,
              galleryViewWithAttach,
              consultationDetails
            }}
          />
        </ZoomMediaContext.Provider>
      )}
    </div>
  );
};

export default ZoomVideoOutlet;
